import { deleteCookie, getCookie, setCookie } from "cookies-next";

import { CART_TOKEN_KEY } from "@/constants/CookieValues";
import {
  BHAPTICS_COOKIE_OPTS,
  deleteCookieAllScopes,
} from "@/libs/locale/cookie";

// Global-E `GetCheckoutCartInfo` 가 호출할 때 사용할 cart 식별자.
// 정책: docs/global-e-storefront.md §4.5
// - 값은 unguessable GUID (RFC 4122 UUID v4) — sequential 금지
// - 저장: cookie, **host 기반 스코프**(`BHAPTICS_COOKIE_OPTS.domain` = resolveCookieDomain(host)).
//   ⚠️ env(isProd)로 스코프를 고정하지 말 것 — dev.bhaptics.com 이 scope drift 를 일으켜
//   `.bhaptics.com`/host-only 로 **bh_cart_token 중복 쿠키**가 생기고, 클라가 write 하는 토큰과
//   서버(`req.cookies`)/GE 가 read 하는 토큰이 갈라져 guest-cart 가 갱신 안 된다(GE 체크아웃이
//   stale 수량 표시). #4993 이 GlobalE_Data/bh_ccode 에 쓴 것과 같은 패턴으로, write/clear 마다
//   `deleteCookieAllScopes` 로 전 스코프를 정리해 항상 bh_cart_token 을 1개로 수렴시킨다.
// - 발급: cart에 첫 아이템 추가 시 / cart 비어있지 않은데 token 없을 때
// - 삭제: cart.clear() (체크아웃 완료 / 사용자 비우기) + GE 주문 소비 시 직접(`consumed-cart.ts` `clearConsumedCart` —
//   바로구매는 빔→빔이거나 카트를 남겨야 해서 cart-store 전이만으로는 교체되지 않는다)

const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30일

// frontend↔mgmt 공유 cart token 형식(RFC 4122 UUID v4). 체크아웃 draft 로 전달할 때
// 서버단(fetchOrderDraft)에서도 같은 검증을 쓰도록 export.
export const CART_TOKEN_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function generateToken(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }
  // Fallback — 구식 브라우저/환경. randomUUID 미지원 시에도 unguessable 보장.
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

function readToken(): string | null {
  const raw = getCookie(CART_TOKEN_KEY);
  if (typeof raw !== "string") return null;
  return CART_TOKEN_UUID_REGEX.test(raw) ? raw : null;
}

function writeToken(token: string): void {
  // 먼저 알려진 모든 스코프의 동일 쿠키를 지워 크로스-스코프 중복을 제거하고(#4993 패턴), host 기반
  // canonical 스코프(BHAPTICS_COOKIE_OPTS.domain)에만 기록 → document.cookie 에 bh_cart_token 이
  // 항상 1개. (env 기반 domain 이 남긴 `.bhaptics.com`/host-only 중복 세션도 다음 write 에 수렴.)
  deleteCookieAllScopes(CART_TOKEN_KEY, deleteCookie);
  setCookie(CART_TOKEN_KEY, token, {
    ...BHAPTICS_COOKIE_OPTS,
    maxAge: COOKIE_MAX_AGE,
  });
}

export const CartToken = {
  get(): string | null {
    return readToken();
  },

  ensure(): string {
    // 기존 토큰이 있어도 writeToken 으로 재기록한다 → 스코프 정규화 + 중복 제거(기존 중복 세션도
    // cart 활동 시 1개로 수렴). 값은 그대로라 idempotent(같은 토큰 유지), maxAge 갱신 부수효과만.
    const token = readToken() ?? generateToken();
    writeToken(token);
    return token;
  },

  clear(): void {
    // 단일 스코프만 지우면 다른 스코프 중복이 남으므로 전 스코프 삭제(#4993).
    deleteCookieAllScopes(CART_TOKEN_KEY, deleteCookie);
  },
};
